import {
  Button,
  IconButton,
  MenuItem,
  Select,
  Stack,
  styled,
  TextField,
  Typography,
} from '@mui/material';
import { type ReactNode, useState } from 'react';
import { DynamicIcon } from 'src/components/DynamicIcon/dynamic-icon';

/** One keypad option of a Phone menu step, as stored in `node.data.options`. */
export interface MenuOptionValue {
  key: string;
  digit: string;
  label: string;
}

export interface MenuOptionsInputProps {
  options: MenuOptionValue[];
  readOnly: boolean;
  onChange: (next: MenuOptionValue[]) => void;
  /** Removes an option with its connection, and the steps only it reached. */
  onRemove: (key: string) => void;
  /** How many steps removing an option would take with it. */
  stepsAfter: (key: string) => number;
  label: ReactNode;
  helper: ReactNode;
}

const ConfirmBox = styled(Stack)(({ theme }) => ({
  border: `1px solid ${theme.palette.error.main}`,
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(1),
  gap: theme.spacing(1),
}));

const stepsText = (count: number) => `${count} ${count === 1 ? 'step' : 'steps'}`;

/** The keys a caller can press, in keypad order. */
const KEYPAD = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '0', '*', '#'];

/** The first `option<n>` no option has, so a new option's connection never points at another. */
const freeKey = (options: MenuOptionValue[]) => {
  const taken = new Set(options.map(({ key }) => key));
  let n = options.length + 1;
  while (taken.has(`option${n}`)) n += 1;
  return `option${n}`;
};

/**
 * The MENU_OPTIONS field: one row per option — the key to press, a label — with add and remove.
 * A new option gets a key of its own, unique in the step, so its connection keeps pointing at it
 * when the label is edited later. Removing an option with steps after it asks first, saying how
 * many go with it.
 */
export const MenuOptionsInput = ({
  options,
  readOnly,
  onChange,
  onRemove,
  stepsAfter,
  label,
  helper,
}: MenuOptionsInputProps) => {
  const [confirmingKey, setConfirmingKey] = useState<string | null>(null);

  if (readOnly) {
    return (
      <Stack spacing={1}>
        {label}
        {options.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            Not set
          </Typography>
        ) : null}
        {options.map((option) => (
          <Typography key={option.key} variant="body2" sx={{ overflowWrap: 'anywhere' }}>
            {option.digit} · {option.label}
          </Typography>
        ))}
        {helper}
      </Stack>
    );
  }

  const update = (index: number, patch: Partial<MenuOptionValue>) =>
    onChange(options.map((option, i) => (i === index ? { ...option, ...patch } : option)));

  const add = () => {
    const usedDigits = new Set(options.map(({ digit }) => digit));
    const digit = KEYPAD.find((key) => !usedDigits.has(key));
    if (!digit) return;
    const key = freeKey(options);
    onChange([...options, { key, digit, label: `Option ${key.slice('option'.length)}` }]);
  };

  const remove = (key: string) => {
    if (stepsAfter(key) > 0) {
      setConfirmingKey(key);
      return;
    }
    onRemove(key);
  };

  const confirming = options.find(({ key }) => key === confirmingKey);

  return (
    <Stack spacing={1}>
      {label}
      {options.map((option, index) => (
        <Stack key={option.key} direction="row" spacing={0.5} alignItems="center">
          <Select
            size="small"
            value={option.digit}
            inputProps={{ 'aria-label': `Key for ${option.label || 'this option'}` }}
            onChange={(event) => update(index, { digit: String(event.target.value) })}
          >
            {KEYPAD.map((key) => (
              <MenuItem key={key} value={key}>
                {key}
              </MenuItem>
            ))}
          </Select>
          <TextField
            size="small"
            placeholder="Label"
            value={option.label}
            inputProps={{ 'aria-label': 'Label' }}
            sx={{ flex: 1 }}
            onChange={(event) => update(index, { label: event.target.value })}
          />
          <IconButton
            size="small"
            aria-label={`Remove ${option.label || 'option'}`}
            disabled={Boolean(confirming)}
            onClick={() => remove(option.key)}
          >
            <DynamicIcon name="close" fontSize="small" />
          </IconButton>
        </Stack>
      ))}
      {confirming ? (
        <ConfirmBox>
          <Typography variant="body2">
            Remove {confirming.digit} · {confirming.label} and the{' '}
            {stepsText(stepsAfter(confirming.key))} after it?
          </Typography>
          <Stack direction="row" gap={0.5}>
            <Button
              size="small"
              color="error"
              variant="outlined"
              onClick={() => {
                setConfirmingKey(null);
                onRemove(confirming.key);
              }}
            >
              Remove
            </Button>
            <Button size="small" onClick={() => setConfirmingKey(null)}>
              Cancel
            </Button>
          </Stack>
        </ConfirmBox>
      ) : null}
      <Button
        size="small"
        disabled={options.length >= KEYPAD.length}
        onClick={add}
        sx={{ alignSelf: 'flex-start' }}
      >
        Add option
      </Button>
      {helper}
    </Stack>
  );
};
